import { commonText, useCommonLanguage } from "../../i18n/commonPresentation";
import { updatesText as up, useUpdatesLanguage, type UpdatesKey } from "../../i18n/updatesPresentation";
/**
 * DashboardUpdateModal
 *
 * Console-style modal for triggering and monitoring a dashboard update.
 * Handles the service restart by switching to recovery polling when
 * the SSE stream disconnects.
 *
 * Features "Idle UX" animation during service restart to give users
 * visual feedback that the system is working even when no new log
 * output is being received.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { ModalCloseButton } from "../ui/ModalCloseButton";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";
import { useDashboardUpdate, type UpdatePhase } from "../../hooks/useDashboardUpdate";
import type { DashboardChannel } from "../../hooks/useSystemMeta";
import { useConsoleScroll } from "../../hooks/useConsoleScroll";
import { ConsolePanel } from "./ConsolePanel";

// ── Idle UX Messages ────────────────────────────────────────────────────────

const IDLE_MESSAGES: Array<{ delay: number; key: UpdatesKey }> = [
  { delay: 3000, key: "idleStop" },
  { delay: 8000, key: "idleDependencies" },
  { delay: 15000, key: "idleBuild" },
  { delay: 25000, key: "idleMigrations" },
  { delay: 35000, key: "idleStart" },
  { delay: 50000, key: "idleHealth" },
  { delay: 70000, key: "idleWait" },
  { delay: 90000, key: "idleLong" },
];

// ── Terminal Cursor Component ───────────────────────────────────────────────

const TerminalCursor = ({ active }: { active: boolean }) => {
  useUpdatesLanguage();
  if (!active) return null;
  return (
    <span className="inline-block w-2 h-4 bg-emerald-400/80 animate-pulse ml-0.5 align-middle" />
  );
};

type DashboardUpdateModalProps = {
  open: boolean;
  onClose: () => void;
  currentCommitShort: string | null;
  latestCommitShort: string | null;
  currentVersion: string | null;
  latestVersion: string | null;
  channel?: DashboardChannel;
  /**
   * Called when the backend escalates to a full `qb update quickbox` (the CLI
   * core is also behind). The caller should close this modal and attach the
   * maintenance modal to the returned jobId. When omitted, escalation falls
   * through to the normal dashboard-update flow.
   */
  onEscalateToFullUpdate?: (jobId: string) => void;
};



const isActivePhase = (phase: UpdatePhase): boolean =>
  phase === "starting" || phase === "streaming" || phase === "reconnecting" || phase === "verifying";

export const DashboardUpdateModal = ({
  open,
  onClose,
  currentCommitShort,
  latestCommitShort,
  currentVersion,
  latestVersion,
  channel,
  onEscalateToFullUpdate,
}: DashboardUpdateModalProps) => {
  useCommonLanguage();
  useUpdatesLanguage();
  const PHASE_BADGES: Record<UpdatePhase, { label: string; className: string }> = {
    idle: { label: up("ready"), className: "bg-muted text-muted-foreground" },
    starting: { label: up("starting"), className: "bg-yellow-500/20 text-yellow-400" },
    streaming: { label: up("updating"), className: "bg-blue-500/20 text-blue-400 animate-pulse" },
    reconnecting: { label: up("restarting"), className: "bg-amber-500/20 text-amber-400 animate-pulse" },
    verifying: { label: up("verifying"), className: "bg-blue-500/20 text-blue-400 animate-pulse" },
    done: { label: up("complete"), className: "bg-emerald-500/20 text-emerald-400" },
    version_unchanged: { label: up("needsAttention"), className: "bg-amber-500/20 text-amber-400" },
    failed: { label: up("failed"), className: "bg-red-500/20 text-red-400" },
  };
  const { phase, lines, displayLines, displayError, newCommit, newVersion, isReinstall, escalatedJobId, startUpdate, reset } = useDashboardUpdate({
    currentCommitShort,
    currentVersion: currentVersion ?? null,
    channel,
  });

  const displayCurrent = currentVersion && currentVersion !== "unknown" ? `v${currentVersion}` : currentCommitShort;
  const displayLatest = latestVersion ? `v${latestVersion}` : latestCommitShort;
  const displayNewVersion = newVersion && newVersion !== "unknown" ? `v${newVersion}` : newCommit;
  const titleId = useId();

  // Idle UX state
  const [idleLines, setIdleLines] = useState<UpdatesKey[]>([]);
  const idleTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Guards the one-shot escalation handoff so it fires once per open cycle.
  const escalationFiredRef = useRef(false);

  const scroll = useConsoleScroll(lines.length + idleLines.length);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      reset();
      setIdleLines([]);
    }
  }, [open, reset]);

  // Escalation handoff: when the backend escalated to a full `qb update quickbox`,
  // hand the maintenance jobId to the caller (which closes this modal and attaches
  // the maintenance modal to the running job). Fires once per open cycle.
  useEffect(() => {
    if (!open) {
      escalationFiredRef.current = false;
      return;
    }
    if (escalatedJobId !== null && !escalationFiredRef.current) {
      escalationFiredRef.current = true;
      onEscalateToFullUpdate?.(escalatedJobId);
    }
  }, [open, escalatedJobId, onEscalateToFullUpdate]);

  // Idle UX: Show placeholder messages during reconnecting phase
  useEffect(() => {
    idleTimersRef.current.forEach((t) => clearTimeout(t));
    idleTimersRef.current = [];

    if (phase === "reconnecting") {
      setIdleLines([]);
      IDLE_MESSAGES.forEach(({ delay, key }) => {
        const timer = setTimeout(() => {
          setIdleLines((prev) => [...prev, key]);
        }, delay);
        idleTimersRef.current.push(timer);
      });
    } else if (phase === "verifying" || phase === "done" || phase === "version_unchanged" || phase === "failed") {
      setIdleLines([]);
    }

    return () => {
      idleTimersRef.current.forEach((t) => clearTimeout(t));
      idleTimersRef.current = [];
    };
  }, [phase]);

  const handleCopyOutput = useCallback(() => {
    scroll.handleCopyOutput(lines);
  }, [lines, scroll]);

  const handleClose = useCallback(() => {
    if (isActivePhase(phase)) return;
    reset();
    onClose();
  }, [phase, reset, onClose]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  if (!open) return null;

  // Use a friendlier badge for pre-release reinstalls
  const badge = phase === "version_unchanged" && isReinstall
    ? { label: up("reinstalled"), className: "bg-blue-500/20 text-blue-400" }
    : PHASE_BADGES[phase];
  const showConfirm = phase === "idle";
  const showConsole = phase !== "idle";
  const isTerminal = phase === "done" || phase === "version_unchanged" || phase === "failed";

  // Idle UX + terminal cursor rendered as afterLines slot
  const afterLinesContent = (
    <>
      {phase === "reconnecting" && idleLines.length > 0 && (
        <div className="mt-2 border-t border-white/[0.06] pt-2">
          {idleLines.map((line, i) => (
            <div
              key={`idle-${i}`}
              className="text-muted-foreground animate-in fade-in-0 duration-500"
            >
              {up(line)}
            </div>
          ))}
        </div>
      )}
      {isActivePhase(phase) && (
        <div className="inline-flex items-center mt-1">
          <span className="text-muted-foreground/30 select-none">{">"}</span>
          <TerminalCursor active />
        </div>
      )}
    </>
  );

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={isTerminal || showConfirm ? handleClose : undefined}
      />
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="relative w-full max-w-3xl mx-4 animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="rounded-xl border border-white/[0.08] bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04]">
                <Icon icon="lucide:download" className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <h2 id={titleId} className="text-sm font-semibold text-foreground">{up("dashboardUpdate")}</h2>
                <p className="text-[10px] text-muted-foreground">{up("systemResponse")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium", badge.className)}>
                {badge.label}
              </span>
              <ModalCloseButton onClick={handleClose} disabled={isActivePhase(phase)} label={up("closeDialog")} />
            </div>
          </div>

          {/* Confirmation view */}
          {showConfirm && (
            <div className="px-5 py-6 space-y-4">
              <p className="text-sm text-foreground">
                {displayLatest ? up("confirmUpdateVersion", { version: displayLatest }) : up("confirmUpdate")}
              </p>

              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-200/90 space-y-1">
                  <p>{up("restartWarning")}</p>
                </div>
              </div>

              {displayCurrent && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{commonText("currentVersionLabel")}<code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[10px]">{displayCurrent}</code></span>
                  {displayLatest && (
                    <>
                      <Icon icon="lucide:arrow-right" className="h-3 w-3" />
                      <span>{commonText("latestVersionLabel")}<code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[10px]">{displayLatest}</code></span>
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  {up("cancel")}</Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void startUpdate()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Icon icon="lucide:download" className="h-3.5 w-3.5 mr-1.5" />
                  {up("updateDashboard")}
                </Button>
              </div>
            </div>
          )}

          {/* Console output view */}
          {showConsole && (
            <>
              <ConsolePanel
                lines={displayLines}
                consoleRef={scroll.consoleRef}
                onScroll={scroll.handleScroll}
                wrapLines={scroll.wrapLines}
                onToggleWrap={() => scroll.setWrapLines((prev) => !prev)}
                autoScroll={scroll.autoScroll}
                onScrollToBottom={scroll.scrollToBottom}
                onCopy={handleCopyOutput}
                isLoading={isActivePhase(phase)}
                afterLines={afterLinesContent}
              />

              {/* Reconnecting overlay */}
              {(phase === "reconnecting" || phase === "verifying") && (
                <div className="flex items-center gap-3 px-5 py-3 border-t border-white/[0.06] bg-amber-500/5">
                  <Spinner size="4" className="text-amber-400" />
                  <span className="text-xs text-amber-400">
                    {phase === "reconnecting"
                      ? up("recovery")
                      : up("verifyingUpdate")}
                  </span>
                </div>
              )}

              {phase === "done" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-muted-foreground">
                    {displayNewVersion ? up("completeVersion", { version: displayNewVersion }) : up("updateComplete")}
                  </div>
                  <Button variant="primary" size="sm" onClick={handleReload}>
                    {up("reloadDashboard")}
                  </Button>
                </div>
              )}

              {phase === "version_unchanged" && isReinstall && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="flex items-start gap-2 flex-1">
                    <Icon icon="lucide:info" className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>{up("reinstallUnchanged", { version: displayNewVersion ?? up("unknown") })}</p>
                      <p>{up("reinstallNormal")}</p>
                    </div>
                  </div>
                  <Button variant="primary" size="sm" onClick={handleReload}>
                    {up("reloadDashboard")}
                  </Button>
                </div>
              )}

              {phase === "version_unchanged" && !isReinstall && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06] bg-amber-500/5">
                  <div className="flex items-start gap-2 flex-1">
                    <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-200/90 space-y-1">
                      <p>{up("unchanged", { version: displayNewVersion ?? up("unknown") })}</p>
                      <p>{up("checkLogs")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCopyOutput}>
                      {up("copyLogs")}</Button>
                    <Button variant="secondary" size="sm" onClick={handleReload}>
                      {up("reloadAnyway")}</Button>
                  </div>
                </div>
              )}

              {phase === "failed" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-red-400/80 flex-1 min-w-0 truncate">
                    {displayError ?? up("updateFailed")}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCopyOutput}>
                      {up("copyLogs")}</Button>
                    <Button variant="secondary" size="sm" onClick={handleClose}>
                      {up("close")}</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
